import { apiRequest, queryClient } from './queryClient';
import { WalletAlert, WalletAlertHistory } from '@shared/schema';

/**
 * Interface for creating a new wallet alert
 * We now only support Bitcoin with transaction-based alert types
 */
interface CreateWalletAlertDto {
  address: string;
  cryptocurrency: 'bitcoin';
  alertType: 'received' | 'sent' | 'both';
  notificationMethod: 'email' | 'sms' | 'both';
  contactInfo: string;
}

export async function createWalletAlert(data: CreateWalletAlertDto): Promise<WalletAlert> {
  const response = await apiRequest('POST', '/api/wallet-alerts', data);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to create wallet alert' }));
    const error = new Error(errorData.message || 'Failed to create wallet alert');
    // Attach the status code and error code to the error for specific handling
    (error as any).status = response.status;
    (error as any).code = errorData.error;
    throw error;
  }
  
  // Invalidate the wallet alerts cache
  queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts'] });
  
  return await response.json();
}

export async function getWalletAlerts(): Promise<WalletAlert[]> {
  const response = await apiRequest('GET', '/api/wallet-alerts');
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch wallet alerts');
  }
  
  return await response.json();
}

export async function deleteWalletAlert(id: number): Promise<void> {
  const response = await apiRequest('DELETE', `/api/wallet-alerts/${id}`);
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete wallet alert');
  }
  
  // Invalidate the wallet alerts cache
  queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts'] });
}

export async function getWalletAlertHistory(limit?: number): Promise<WalletAlertHistory[]> {
  const url = limit ? `/api/wallet-alerts/history?limit=${limit}` : '/api/wallet-alerts/history';
  const response = await apiRequest('GET', url);
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch wallet alert history');
  }
  
  return await response.json();
}

export async function triggerWalletAlert(id: number): Promise<void> {
  const response = await apiRequest('POST', `/api/wallet-alerts/trigger/${id}`);
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to trigger wallet alert');
  }
  
  // Invalidate both the wallet alerts and history cache
  queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts'] });
  queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts/history'] });
}